import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { adminAPI } from '../api';

export default function TransferSettings() {
  const [settings, setSettings]   = useState({});
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);

  // Local form state
  const [minTransfer, setMinTransfer]   = useState('');
  const [feeRate, setFeeRate]           = useState('');

  useEffect(() => {
    adminAPI.settings().then(res => {
      const map = {};
      (res.data.settings || []).forEach(s => { map[s.setting_key] = s.setting_value; });
      setSettings(map);
      setMinTransfer(map.min_transfer    ?? '1');
      setFeeRate(map.transfer_fee_rate   ?? '0');
    }).catch(() => toast.error('Failed to load settings'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    const minVal = parseFloat(minTransfer);
    const feeVal = parseFloat(feeRate);

    if (isNaN(minVal) || minVal < 0) {
      toast.error('Minimum transfer amount must be 0 or greater.');
      return;
    }
    if (isNaN(feeVal) || feeVal < 0 || feeVal > 100) {
      toast.error('Transfer fee rate must be between 0 and 100.');
      return;
    }

    setSaving(true);
    try {
      await adminAPI.saveSettings([
        { key: 'min_transfer',       value: String(minVal) },
        { key: 'transfer_fee_rate',  value: String(feeVal) },
      ]);
      toast.success('Transfer settings saved.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ padding: 40, color: 'var(--gray-500)' }}>Loading settings...</div>;
  }

  return (
    <div>
      <p style={{ color: 'var(--gray-500)', marginBottom: 24, fontSize: 14 }}>
        Configure the peer-to-peer balance transfer feature. These values take effect
        immediately for all users. Funds sent by a user are credited to the
        recipient's Deposit Balance. The fee is deducted from the sender's transfer
        amount before the recipient receives the net total.
      </p>

      <div className="settings-card">
        <div className="settings-section-title">Transfer Rules</div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Minimum Transfer Amount (GHS)</label>
            <input
              className="form-control"
              type="number"
              min="0"
              step="0.01"
              value={minTransfer}
              onChange={e => setMinTransfer(e.target.value)}
              placeholder="e.g. 10"
            />
            <div className="form-hint">
              Users cannot initiate a transfer below this amount. Set to 0 to allow
              any amount.
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Transfer Fee Rate (%)</label>
            <input
              className="form-control"
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={feeRate}
              onChange={e => setFeeRate(e.target.value)}
              placeholder="e.g. 2"
            />
            <div className="form-hint">
              Percentage deducted from the sender's amount as a platform fee. Set to
              0 for fee-free transfers.
            </div>
          </div>
        </div>

        {/* Live preview */}
        {parseFloat(minTransfer) >= 0 && parseFloat(feeRate) >= 0 && (
          <div className="preview-box">
            <div className="preview-title">Example Preview</div>
            <PreviewRow label="Sender sends" value={`GHS 100.00`} />
            <PreviewRow
              label={`Transfer fee (${feeRate || 0}%)`}
              value={`- GHS ${((100 * parseFloat(feeRate || 0)) / 100).toFixed(2)}`}
            />
            <div className="preview-divider" />
            <PreviewRow
              label="Recipient receives"
              value={`GHS ${(100 - (100 * parseFloat(feeRate || 0)) / 100).toFixed(2)}`}
              bold
            />
          </div>
        )}

        <button
          className="btn-save"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Transfer Settings'}
        </button>
      </div>

      <style>{`
        .settings-card {
          background: #fff;
          border-radius: 12px;
          padding: 28px;
          box-shadow: 0 2px 12px rgba(0,0,0,0.06);
          max-width: 680px;
        }
        .settings-section-title {
          font-size: 16px;
          font-weight: 700;
          color: var(--gray-800, #1a202c);
          margin-bottom: 20px;
          padding-bottom: 12px;
          border-bottom: 1px solid #eee;
        }
        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
        }
        .form-group { margin-bottom: 20px; }
        .form-label {
          display: block;
          font-size: 13px;
          font-weight: 600;
          color: #374151;
          margin-bottom: 6px;
        }
        .form-control {
          width: 100%;
          padding: 10px 14px;
          border: 1.5px solid #e5e7eb;
          border-radius: 8px;
          font-size: 15px;
          outline: none;
          transition: border-color 0.2s;
          box-sizing: border-box;
        }
        .form-control:focus { border-color: #2d6bc4; }
        .form-hint {
          font-size: 12px;
          color: #6b7280;
          margin-top: 5px;
          line-height: 1.5;
        }
        .preview-box {
          background: #f8faff;
          border: 1px solid #dce8fb;
          border-radius: 10px;
          padding: 16px 20px;
          margin-bottom: 24px;
        }
        .preview-title {
          font-size: 12px;
          font-weight: 700;
          color: #2d6bc4;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 10px;
        }
        .preview-divider {
          height: 1px;
          background: #dce8fb;
          margin: 8px 0;
        }
        .btn-save {
          background: linear-gradient(135deg, #1e4d99, #2d6bc4);
          color: #fff;
          border: none;
          border-radius: 8px;
          padding: 12px 28px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: opacity 0.2s;
        }
        .btn-save:disabled { opacity: 0.6; cursor: not-allowed; }
        .btn-save:hover:not(:disabled) { opacity: 0.9; }
        @media (max-width: 600px) {
          .form-row { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
}

function PreviewRow({ label, value, bold }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
      <span style={{ fontSize: 13, color: '#6b7280' }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: bold ? 700 : 500, color: bold ? '#111827' : '#374151' }}>
        {value}
      </span>
    </div>
  );
}
